import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  CreateClientContactInput,
  OrganizationStatus,
  OrganizationType,
  UpdateOrganizationInput,
} from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import { PhoneInput } from '../components/PhoneInput';
import {
  useClientContacts,
  useCreateClientContact,
  useDeleteOrganization,
  useOrganization,
  useUpdateOrganization,
} from '../api/queries/clients';
import { useSites } from '../api/queries/sites';
import { usePrincipals } from '../api/queries/principals';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
  Table,
  Textarea,
} from '../components/ui';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';

const ORG_TYPES: OrganizationType[] = [
  'corporate',
  'hni',
  'government',
  'event',
  'property_mgmt',
  'other',
];
const ORG_STATUSES: OrganizationStatus[] = ['prospect', 'active', 'inactive'];

export function OrganizationDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const orgId = id ? Number(id) : null;

  const { data, isLoading, isError, error } = useOrganization(orgId);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const del = useDeleteOrganization();

  const canWrite = can('org.write');
  const canDelete = can('org.delete');
  const canViewPrincipals = can('principal.read');

  if (isLoading) return <p className="muted">Loading…</p>;
  if (isError || !data) {
    return (
      <p className="error-text" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load client.'}
      </p>
    );
  }

  const org = data.organization;

  const onDelete = async () => {
    if (orgId === null) return;
    try {
      await del.mutateAsync(orgId);
      toast.success('Client deleted.');
      setConfirmDelete(false);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to delete client.');
    }
  };

  return (
    <div className="page organization-detail-page">
      <nav className="breadcrumbs">
        {org.type === 'hni' ? (
          <Link to="/directory/individuals">Individuals</Link>
        ) : (
          <Link to="/directory/businesses">Businesses</Link>
        )}
        <span aria-hidden="true"> / </span>
        <span>{org.name}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1>{org.name}</h1>
          <p className="page-sub">
            <StatusBadge status={org.status}>{org.status}</StatusBadge>{' '}
            {org.type} {org.industry ? `· ${org.industry}` : ''}
          </p>
        </div>
        <div className="header-actions">
          {(can('portal.manage_org_users', orgId ?? undefined) ||
            can('settings.manage_users')) && (
            <Button asChild variant="ghost">
              <Link to={`/orgs/${org.id}/portal-users`}>Portal users</Link>
            </Button>
          )}
          {canWrite && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditOpen(true)}
            >
              Edit
            </button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </header>

      <section className="detail-card">
        <h2>Overview</h2>
        <dl className="detail-grid">
          <div>
            <dt>Legal name</dt>
            <dd>{org.legal_name ?? '—'}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{org.type}</dd>
          </div>
          <div>
            <dt>Industry</dt>
            <dd>{org.industry ?? '—'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{org.status}</dd>
          </div>
          <div className="detail-grid-full">
            <dt>Tags</dt>
            <dd>
              {org.tags.length > 0 ? (
                <div className="tag-list">
                  {org.tags.map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                </div>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div className="detail-grid-full">
            <dt>Notes</dt>
            <dd>{org.notes ?? '—'}</dd>
          </div>
        </dl>
      </section>

      <ContactsSection orgId={org.id} canWrite={canWrite} />

      <SitesSection orgId={org.id} />

      {canViewPrincipals && <PrincipalsSection orgId={org.id} />}

      {editOpen && (
        <EditOrganizationModal
          orgId={org.id}
          initial={{
            name: org.name,
            legal_name: org.legal_name,
            type: org.type,
            industry: org.industry,
            status: org.status,
            tags: org.tags,
            notes: org.notes,
          }}
          onClose={() => setEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete client"
        message={`Delete "${org.name}"? This soft-deletes the client and hides it from lists.`}
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

function ContactsSection({
  orgId,
  canWrite,
}: {
  orgId: number;
  canWrite: boolean;
}): JSX.Element {
  const { data, isLoading } = useClientContacts(orgId);
  const [addOpen, setAddOpen] = useState(false);
  const contacts = data?.contacts ?? [];

  return (
    <section className="detail-card">
      <div className="section-header">
        <h2>Contacts</h2>
        {canWrite && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setAddOpen(true)}
          >
            Add contact
          </button>
        )}
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && contacts.length === 0 && (
        <p className="muted">No contacts recorded.</p>
      )}

      {contacts.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Phone</Table.HeaderCell>
              <Table.HeaderCell>Flags</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {contacts.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell tone="strong">{c.name}</Table.Cell>
                <Table.Cell tone="muted">{c.role ?? '—'}</Table.Cell>
                <Table.Cell tone="muted">{c.email ?? '—'}</Table.Cell>
                <Table.Cell tone="muted">
                  {c.phone ? formatPhoneDisplay(c.phone) : '—'}
                </Table.Cell>
                <Table.Cell>
                  {c.is_primary && <Badge tone="info">Primary</Badge>}{' '}
                  {c.is_emergency && <Badge tone="warn">Emergency</Badge>}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {addOpen && (
        <AddContactModal orgId={orgId} onClose={() => setAddOpen(false)} />
      )}
    </section>
  );
}

function AddContactModal({
  orgId,
  onClose,
}: {
  orgId: number;
  onClose: () => void;
}): JSX.Element {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);

  const create = useCreateClientContact();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const input: CreateClientContactInput = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    };
    if (role.trim()) input.role = role.trim();
    if (email.trim()) input.email = email.trim();
    if (phone.trim()) input.phone = phone.trim();
    if (isPrimary) input.is_primary = true;
    if (isEmergency) input.is_emergency = true;

    try {
      await create.mutateAsync({ orgId, ...input });
      toast.success('Contact added.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to add contact.');
    }
  };

  return (
    <Dialog
      title="Add contact"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-contact-form"
            variant="primary"
            disabled={
              create.isPending ||
              firstName.trim().length === 0 ||
              lastName.trim().length === 0
            }
          >
            {create.isPending ? 'Saving…' : 'Add'}
          </Button>
        </>
      }
    >
      <form id="add-contact-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="First name" required>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Last name" required>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </FormField>
        <FormField label="Role">
          <Input value={role} onChange={(e) => setRole(e.target.value)} />
        </FormField>
        <FormField label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Phone">
          <PhoneInput value={phone} onChange={setPhone} />
        </FormField>
        <label className="form-check">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
          />
          <span>Primary contact</span>
        </label>
        <label className="form-check">
          <input
            type="checkbox"
            checked={isEmergency}
            onChange={(e) => setIsEmergency(e.target.checked)}
          />
          <span>Emergency contact</span>
        </label>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sites (this org's posting locations)
// ---------------------------------------------------------------------------

function SitesSection({ orgId }: { orgId: number }): JSX.Element {
  const { data, isLoading } = useSites({ organization_id: orgId });
  const sites = data?.sites ?? [];

  return (
    <section className="detail-card">
      <div className="section-header">
        <h2>Sites</h2>
        <Button asChild variant="ghost" className="btn-sm">
          <Link to={`/sites?organization_id=${orgId}`}>View all</Link>
        </Button>
      </div>
      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && sites.length === 0 && (
        <p className="muted">No sites for this client.</p>
      )}
      {sites.length > 0 && (
        <ul className="link-list">
          {sites.map((s) => (
            <li key={s.id}>
              <Link to={`/sites/${s.id}`}>{s.name}</Link>{' '}
              <span className="muted small">
                {s.site_type} · {s.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Principals (protectees tied to this org) — gated on principal.read by caller
// ---------------------------------------------------------------------------

function PrincipalsSection({ orgId }: { orgId: number }): JSX.Element {
  const { data, isLoading } = usePrincipals({ organization_id: orgId });
  const principals = data?.principals ?? [];

  return (
    <section className="detail-card">
      <div className="section-header">
        <h2>Principals</h2>
        <Button asChild variant="ghost" className="btn-sm">
          <Link to={`/directory/individuals?organization_id=${orgId}`}>View all</Link>
        </Button>
      </div>
      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && principals.length === 0 && (
        <p className="muted">No principals for this client.</p>
      )}
      {principals.length > 0 && (
        <ul className="link-list">
          {principals.map((p) => (
            <li key={p.id}>
              <Link to={`/principals/${p.id}`}>{p.full_name}</Link>{' '}
              <StatusBadge status={`risk-${p.risk_level}`}>
                {p.risk_level}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit org
// ---------------------------------------------------------------------------

function EditOrganizationModal({
  orgId,
  initial,
  onClose,
}: {
  orgId: number;
  initial: {
    name: string;
    legal_name: string | null;
    type: OrganizationType;
    industry: string | null;
    status: OrganizationStatus;
    tags: string[];
    notes: string | null;
  };
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial.name);
  const [legalName, setLegalName] = useState(initial.legal_name ?? '');
  const [type, setType] = useState<OrganizationType>(initial.type);
  const [industry, setIndustry] = useState(initial.industry ?? '');
  const [status, setStatus] = useState<OrganizationStatus>(initial.status);
  const [tags, setTags] = useState(initial.tags.join(', '));
  const [notes, setNotes] = useState(initial.notes ?? '');

  const update = useUpdateOrganization();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    const patch: UpdateOrganizationInput = {
      name: name.trim(),
      legal_name: legalName.trim() || null,
      type,
      industry: industry.trim() || null,
      status,
      tags: parsedTags,
      notes: notes.trim() || null,
    };
    try {
      await update.mutateAsync({ id: orgId, ...patch });
      toast.success('Client updated.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update client.');
    }
  };

  return (
    <Dialog
      title="Edit client"
      open
      onClose={onClose}
      busy={update.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-org-form"
            variant="primary"
            disabled={update.isPending || name.trim().length === 0}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="edit-org-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Legal name">
          <Input
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
        </FormField>
        <FormField label="Type">
          <select
            className="form-input"
            value={type}
            onChange={(e) => setType(e.target.value as OrganizationType)}
          >
            {ORG_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Status">
          <select
            className="form-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as OrganizationStatus)}
          >
            {ORG_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Industry">
          <Input
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
          />
        </FormField>
        <FormField label="Tags (comma-separated)">
          <Input value={tags} onChange={(e) => setTags(e.target.value)} />
        </FormField>
        <FormField label="Notes" full>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormField>
      </form>
    </Dialog>
  );
}
